import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Search, Filter, Plus, ExternalLink, Calendar, DollarSign, TrendingUp, Check, X, RefreshCw, Clock, Settings, Hash, Building2, Folder, Globe, Upload, Download, UserPlus, Loader2 } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'

const Tenders = () => {
  const navigate = useNavigate()
  const [tenders, setTenders] = useState([])
  const [pendingTenders, setPendingTenders] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [orgFilter, setOrgFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [keywordFilter, setKeywordFilter] = useState([])
  const [keywordInput, setKeywordInput] = useState('')
  const [useSemanticSearch, setUseSemanticSearch] = useState(false)
  const [selectedTender, setSelectedTender] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showPendingPanel, setShowPendingPanel] = useState(false)
  const [fetchingGem, setFetchingGem] = useState(false)
  const [showKeywordsModal, setShowKeywordsModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [gemKeywords, setGemKeywords] = useState([])
  const [newKeyword, setNewKeyword] = useState('')
  const [users, setUsers] = useState([])
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assigningTender, setAssigningTender] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const observerRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const searchInputRef = useRef(null)
  const ITEMS_PER_PAGE = 50
  const [newTender, setNewTender] = useState({
    title: '',
    organization: '',
    description: '',
    category: '',
    deadline: '',
    estimatedValue: { amount: 0, currency: 'INR' },
    status: 'New'
  })

  // Debounce search term and maintain focus
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      // Maintain focus on search input after search triggers
      if (searchTerm && searchInputRef.current) {
        searchInputRef.current.focus()
      }
    }, 400)
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Initial load and filter changes
  useEffect(() => {
    setPage(1)
    fetchTenders(1, false)
  }, [statusFilter, categoryFilter, orgFilter, departmentFilter, debouncedSearch, keywordFilter, useSemanticSearch])

  // Fetch pending tenders and other data on mount
  useEffect(() => {
    fetchPendingTenders()
    fetchGemKeywords()
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users?isActive=true')
      setUsers(response.data || [])
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }

  const handleAssignTender = async (userId) => {
    if (!assigningTender) return
    try {
      await api.post(`/users/${userId}/assign-tender/${assigningTender._id}`)
      toast.success('Tender assigned successfully')
      setShowAssignModal(false)
      setAssigningTender(null)
      fetchTenders()
    } catch (error) {
      toast.error('Failed to assign tender')
    }
  }

  const handleUnassignTender = async (tender) => {
    if (!tender.assignedTo) return
    try {
      await api.delete(`/users/${tender.assignedTo._id || tender.assignedTo}/assign-tender/${tender._id}`)
      toast.success('Tender unassigned')
      fetchTenders()
    } catch (error) {
      toast.error('Failed to unassign tender')
    }
  }

  const fetchTenders = async (pageNum = 1, append = false) => {
    if (pageNum === 1 && !append) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    try {
      let response
      
      // Use semantic search if enabled and there's a search term
      if (useSemanticSearch && debouncedSearch) {
        const params = {
          q: debouncedSearch,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          organization: orgFilter !== 'all' ? orgFilter : undefined,
          department: departmentFilter !== 'all' ? departmentFilter : undefined,
          limit: ITEMS_PER_PAGE,
          page: pageNum,
          mode: 'hybrid'
        }
        response = await api.get('/tenders/search/semantic', { params })
      } else {
        const params = {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          organization: orgFilter !== 'all' ? orgFilter : undefined,
          department: departmentFilter !== 'all' ? departmentFilter : undefined,
          search: debouncedSearch || undefined,
          keywords: keywordFilter.length > 0 ? keywordFilter.join(',') : undefined,
          limit: ITEMS_PER_PAGE,
          page: pageNum
        }
        response = await api.get('/tenders', { params })
      }
      
      const data = response.data?.tenders || response.data || []
      const pagination = response.data?.pagination || {}
      
      if (append) {
        setTenders(prev => [...prev, ...data])
      } else {
        setTenders(data)
      }
      
      setHasMore(pagination.hasMore || data.length === ITEMS_PER_PAGE)
      setTotalCount(pagination.totalCount || data.length)
      setPage(pageNum)
    } catch (error) {
      console.error('Failed to fetch tenders:', error)
      if (pageNum === 1) {
        setTenders([])
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const fetchPendingTenders = async () => {
    try {
      const response = await api.get('/tenders/gem/pending')
      setPendingTenders(response.data)
    } catch (error) {
      console.error('Failed to fetch pending tenders:', error)
    }
  }

  const fetchGemKeywords = async () => {
    try {
      const response = await api.get('/tenders/gem/keywords')
      setGemKeywords(response.data.keywords || [])
    } catch (error) {
      console.error('Failed to fetch GEM keywords:', error)
    }
  }

  const handleFetchGemTenders = async () => {
    setFetchingGem(true)
    try {
      const response = await api.post('/tenders/gem/fetch')
      toast.success(`Fetched ${response.data.saved} new tenders from GEM portal`)
      fetchPendingTenders()
    } catch (error) {
      console.error('Failed to fetch GEM tenders:', error)
      toast.error('Failed to fetch tenders from GEM portal')
    } finally {
      setFetchingGem(false)
    }
  }

  const handleAcceptTender = async (tenderId, e) => {
    e.stopPropagation()
    try {
      await api.post(`/tenders/${tenderId}/accept`)
      toast.success('Tender accepted')
      fetchPendingTenders()
      fetchTenders()
    } catch (error) {
      console.error('Failed to accept tender:', error)
      toast.error('Failed to accept tender')
    }
  }

  const handleDiscardTender = async (tenderId, e) => {
    e.stopPropagation()
    try {
      await api.post(`/tenders/${tenderId}/discard`)
      toast.success('Tender discarded')
      fetchPendingTenders()
    } catch (error) {
      console.error('Failed to discard tender:', error)
      toast.error('Failed to discard tender')
    }
  }

  const handleBulkAction = async (action) => {
    const tenderIds = pendingTenders.map(t => t._id)
    if (tenderIds.length === 0) return
    
    try {
      await api.post('/tenders/gem/bulk-review', { tenderIds, action })
      toast.success(`${tenderIds.length} tenders ${action}ed`)
      fetchPendingTenders()
      if (action === 'accept') fetchTenders()
    } catch (error) {
      console.error(`Failed to ${action} tenders:`, error)
      toast.error(`Failed to ${action} tenders`)
    }
  }

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !gemKeywords.includes(newKeyword.trim())) {
      setGemKeywords([...gemKeywords, newKeyword.trim()])
      setNewKeyword('')
    }
  }

  const handleRemoveKeyword = (keyword) => {
    setGemKeywords(gemKeywords.filter(k => k !== keyword))
  }

  const handleSaveKeywords = async () => {
    try {
      await api.put('/tenders/gem/keywords', { keywords: gemKeywords })
      toast.success('Keywords updated successfully')
      setShowKeywordsModal(false)
    } catch (error) {
      console.error('Failed to save keywords:', error)
      toast.error('Failed to save keywords')
    }
  }

  // Get unique values for filters
  const categories = [...new Set(tenders.map(t => t.category).filter(Boolean))]
  const organizations = [...new Set(tenders.map(t => t.organization).filter(Boolean))]
  const departments = [...new Set(tenders.map(t => t.department).filter(Boolean))]

  // Infinite scroll observer
  const lastTenderRef = useCallback(node => {
    if (loading || loadingMore) return
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchTenders(page + 1, true)
      }
    })
    if (node) observerRef.current.observe(node)
  }, [loading, loadingMore, hasMore, page])

  // Use tenders directly since filtering is done server-side now
  const displayTenders = tenders

  const getStatusColor = (status) => {
    const colors = {
      'Pending Review': 'bg-amber-100 text-amber-700',
      'New': 'bg-blue-100 text-blue-700',
      'Under Review': 'bg-yellow-100 text-yellow-700',
      'Go': 'bg-green-100 text-green-700',
      'No-Go': 'bg-red-100 text-red-700',
      'Bid Submitted': 'bg-purple-100 text-purple-700',
      'Won': 'bg-green-100 text-green-700',
      'Lost': 'bg-gray-100 text-gray-700',
      'Discarded': 'bg-gray-100 text-gray-500'
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  const getRelevanceColor = (score) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const handleCreateTender = async (e) => {
    e.preventDefault()
    try {
      // Format data to match backend model
      const tenderData = {
        title: newTender.title,
        organization: newTender.organization,
        description: newTender.description || newTender.title,
        category: newTender.category || 'General',
        deadline: newTender.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        estimatedValue: newTender.estimatedValue?.amount || 0,
        currency: newTender.estimatedValue?.currency || 'INR',
        source: 'Other',
        sourceUrl: 'https://manual-entry.local/' + Date.now(),
        status: 'New'
      }
      await api.post('/tenders', tenderData)
      toast.success('Tender created successfully!')
      setShowAddModal(false)
      setNewTender({
        title: '',
        organization: '',
        description: '',
        category: '',
        deadline: '',
        estimatedValue: { amount: 0, currency: 'INR' },
        status: 'New'
      })
      fetchTenders()
    } catch (error) {
      console.error('Failed to create tender:', error)
      toast.error(error.response?.data?.error || 'Failed to create tender')
    }
  }

  // Only show full-page loading on initial mount, not on filter changes
  const isInitialLoad = loading && tenders.length === 0 && !debouncedSearch && statusFilter === 'all'

  if (isInitialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Tenders</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track and manage tender opportunities</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Pending Tenders Badge */}
          {pendingTenders.length > 0 && (
            <button
              onClick={() => setShowPendingPanel(!showPendingPanel)}
              className="flex items-center px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 border border-amber-300"
            >
              <Clock className="w-5 h-5 mr-2" />
              {pendingTenders.length} Pending Review
            </button>
          )}
          {/* GEM Fetch Button - Disabled */}
          {/* 
          <button
            onClick={handleFetchGemTenders}
            disabled={fetchingGem}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${fetchingGem ? 'animate-spin' : ''}`} />
            {fetchingGem ? 'Fetching...' : 'Fetch GEM Tenders'}
          </button>
          <button
            onClick={() => setShowKeywordsModal(true)}
            className="flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            title="Configure GEM Keywords"
          >
            <Settings className="w-5 h-5" />
          </button>
          */}
          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            title="Bulk Upload Tenders"
          >
            <Upload className="w-5 h-5 mr-2" />
            Bulk Upload
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Tender
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={useSemanticSearch ? "AI-powered semantic search..." : "Search tenders..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <button
              onClick={() => setUseSemanticSearch(!useSemanticSearch)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                useSemanticSearch 
                  ? 'bg-primary-600 text-white hover:bg-primary-700' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={useSemanticSearch ? "Using AI-powered semantic search" : "Using text search"}
            >
              <TrendingUp className="w-4 h-4" />
              {useSemanticSearch ? 'AI Search' : 'Text Search'}
            </button>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Status</option>
              <option value="Pending Review">Pending Review</option>
              <option value="New">New</option>
              <option value="Under Review">Under Review</option>
              <option value="Go">Go</option>
              <option value="No-Go">No-Go</option>
              <option value="Bid Submitted">Bid Submitted</option>
              <option value="Discarded">Discarded</option>
            </select>
          </div>
          {/* Additional Filters Row */}
          <div className="flex flex-wrap gap-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Organizations</option>
              {organizations.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            {(categoryFilter !== 'all' || orgFilter !== 'all' || departmentFilter !== 'all' || keywordFilter.length > 0) && (
              <button
                onClick={() => { setCategoryFilter('all'); setOrgFilter('all'); setDepartmentFilter('all'); setKeywordFilter([]); }}
                className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
              >
                Clear Filters
              </button>
            )}
          </div>
          {/* Keyword Filter */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600 dark:text-gray-400">Keywords:</span>
              {keywordFilter.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => setKeywordFilter(prev => prev.filter((_, i) => i !== index))}
                    className="hover:text-primary-900 dark:hover:text-primary-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && keywordInput.trim()) {
                      e.preventDefault()
                      if (!keywordFilter.includes(keywordInput.trim())) {
                        setKeywordFilter(prev => [...prev, keywordInput.trim()])
                      }
                      setKeywordInput('')
                    }
                  }}
                  placeholder="Add keyword..."
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-32"
                />
                <button
                  onClick={() => {
                    if (keywordInput.trim() && !keywordFilter.includes(keywordInput.trim())) {
                      setKeywordFilter(prev => [...prev, keywordInput.trim()])
                      setKeywordInput('')
                    }
                  }}
                  className="p-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                  title="Add keyword"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tenders Grid */}
      <div className="grid grid-cols-1 gap-4 relative">
        {/* Loading overlay for filter/search operations */}
        {loading && tenders.length > 0 && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center rounded-xl">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Updating...</span>
            </div>
          </div>
        )}
        {displayTenders.length === 0 && !loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Folder className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {debouncedSearch ? `No tenders found for "${debouncedSearch}"` : 'No tenders found'}
            </p>
          </div>
        ) : (
          displayTenders.map((tender, index) => (
          <div
            key={tender._id}
            ref={index === displayTenders.length - 1 ? lastTenderRef : null}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/tenders/${tender._id}`)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{tender.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tender.status)}`}>
                    {tender.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tender.organization}</p>
              </div>
              <div className="flex items-center">
                <TrendingUp className={`w-5 h-5 mr-2 ${getRelevanceColor(tender.aiRelevanceScore)}`} />
                <span className={`text-lg font-bold ${getRelevanceColor(tender.aiRelevanceScore)}`}>
                  {tender.aiRelevanceScore || 0}%
                </span>
              </div>
            </div>

            {/* Info Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {/* Bid Number / Tender Number - Primary identifier */}
              {(tender.gemBidId || tender.tenderNumber) && (
                <span className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                  <Hash className="w-3 h-3 mr-1" />
                  Bid: {tender.gemBidId || tender.tenderNumber}
                </span>
              )}
              {tender.source && (
                <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                  <Globe className="w-3 h-3 mr-1" />
                  {tender.source}
                </span>
              )}
              {tender.category && (
                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                  <Folder className="w-3 h-3 mr-1" />
                  {tender.category}
                </span>
              )}
              {tender.department && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                  <Building2 className="w-3 h-3 mr-1" />
                  {tender.department}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-2">{tender.description}</p>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Release Date</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {tender.publishDate ? format(new Date(tender.publishDate), 'MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Due Date</span>
                <span className="text-sm font-medium text-red-600 dark:text-red-400">
                  {tender.deadline ? format(new Date(tender.deadline), 'MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Estimated Value</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {tender.currency || 'INR'} {tender.estimatedValue ? (tender.estimatedValue >= 10000000 ? `${(tender.estimatedValue / 10000000).toFixed(2)} Cr` : `${(tender.estimatedValue / 100000).toFixed(2)} L`) : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Organization</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={tender.organization}>
                  {tender.organization}
                </span>
              </div>
            </div>

            {tender.aiAnalysis?.recommendation && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-900 dark:text-blue-300">
                  <span className="font-semibold">AI Recommendation:</span> {tender.aiAnalysis.recommendation}
                </p>
              </div>
            )}

            <div className="flex gap-2 flex-wrap items-center">
              <button className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                View Details
              </button>
              <button 
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/tenders/${tender._id}`)
                }}
              >
                Generate Proposal
              </button>
              {/* Assign Button */}
              <button 
                className="flex items-center px-3 py-1 text-sm border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30"
                onClick={(e) => {
                  e.stopPropagation()
                  setAssigningTender(tender)
                  setShowAssignModal(true)
                }}
              >
                <UserPlus className="w-4 h-4 mr-1" />
                {tender.assignedTo?.name ? `Assigned: ${tender.assignedTo.name}` : 'Assign'}
              </button>
              {tender.sourceUrl && !tender.sourceUrl.includes('manual-entry') && (
                <a
                  href={tender.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto flex items-center px-3 py-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View Source
                </a>
              )}
            </div>
          </div>
        ))
        )}
        
        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading more tenders...</span>
          </div>
        )}
        
        {/* Total count */}
        {displayTenders.length > 0 && (
          <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
            Showing {displayTenders.length} of {totalCount} tenders
            {debouncedSearch && <span> matching "{debouncedSearch}"</span>}
          </div>
        )}
      </div>

      {/* Pending Tenders Panel */}
      {showPendingPanel && pendingTenders.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-amber-800">Pending Review from GEM Portal</h2>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('accept')}
                className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <Check className="w-4 h-4 mr-1" />
                Accept All
              </button>
              <button
                onClick={() => handleBulkAction('discard')}
                className="flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                <X className="w-4 h-4 mr-1" />
                Discard All
              </button>
              <button
                onClick={() => setShowPendingPanel(false)}
                className="px-3 py-1 border border-amber-300 rounded-lg hover:bg-amber-100 text-sm"
              >
                Hide
              </button>
            </div>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pendingTenders.map((tender) => (
              <div
                key={tender._id}
                className="bg-white rounded-lg border border-amber-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{tender.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{tender.organization}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Due: {format(new Date(tender.deadline), 'MMM dd, yyyy')}
                      </span>
                      <span className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {tender.currency} {(tender.estimatedValue / 100000).toFixed(2)}L
                      </span>
                      <span className={`font-medium ${tender.aiRelevanceScore >= 70 ? 'text-green-600' : tender.aiRelevanceScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {tender.aiRelevanceScore}% Match
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={(e) => handleAcceptTender(tender._id, e)}
                      className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      title="Accept"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => handleDiscardTender(tender._id, e)}
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      title="Discard"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <a
                      href={tender.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      title="View on GEM"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keywords Modal */}
      {showKeywordsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">GEM Search Keywords</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Configure keywords to search for tenders on the GEM portal. Tenders matching these keywords will be fetched daily.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                placeholder="Add keyword..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <button
                onClick={handleAddKeyword}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-6 min-h-[60px] p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {gemKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="flex items-center px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="ml-2 hover:text-primary-900 dark:hover:text-primary-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
              {gemKeywords.length === 0 && (
                <span className="text-gray-400 text-sm">No keywords configured</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowKeywordsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveKeywords}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Save Keywords
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tender Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Add New Tender</h3>
            <form onSubmit={handleCreateTender} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                <input
                  type="text"
                  value={newTender.title}
                  onChange={(e) => setNewTender({...newTender, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization *</label>
                <input
                  type="text"
                  value={newTender.organization}
                  onChange={(e) => setNewTender({...newTender, organization: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={newTender.description}
                  onChange={(e) => setNewTender({...newTender, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <input
                  type="text"
                  value={newTender.category}
                  onChange={(e) => setNewTender({...newTender, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., IT Services, Construction, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deadline</label>
                <input
                  type="date"
                  value={newTender.deadline}
                  onChange={(e) => setNewTender({...newTender, deadline: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated Value</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={newTender.estimatedValue.amount}
                    onChange={(e) => setNewTender({...newTender, estimatedValue: {...newTender.estimatedValue, amount: parseFloat(e.target.value)}})}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Amount"
                  />
                  <select
                    value={newTender.estimatedValue.currency}
                    onChange={(e) => setNewTender({...newTender, estimatedValue: {...newTender.estimatedValue, currency: e.target.value}})}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option>INR</option>
                    <option>USD</option>
                    <option>EUR</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Tender
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal 
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false)
            fetchTenders()
          }}
        />
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <AssignModal
          tender={assigningTender}
          users={users}
          onAssign={handleAssignTender}
          onUnassign={handleUnassignTender}
          onClose={() => {
            setShowAssignModal(false)
            setAssigningTender(null)
          }}
        />
      )}
    </div>
  )
}

// Bulk Upload Modal Component
const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [uploadLogs, setUploadLogs] = useState([])
  const [showLogs, setShowLogs] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    fetchUploadLogs()
  }, [])

  const fetchUploadLogs = async () => {
    try {
      const response = await api.get('/tenders/bulk-upload/logs?limit=10')
      setUploadLogs(response.data.logs || [])
    } catch (error) {
      console.error('Failed to fetch upload logs:', error)
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    validateAndSetFile(selectedFile)
  }

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return
    
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]
    const validExtensions = ['.csv', '.xlsx', '.xls']
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'))
    
    if (validTypes.includes(selectedFile.type) || validExtensions.includes(ext)) {
      setFile(selectedFile)
      setUploadResult(null)
    } else {
      toast.error('Please select a valid CSV or Excel file (.csv, .xlsx, .xls)')
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFile = e.dataTransfer.files[0]
    validateAndSetFile(droppedFile)
  }

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file')
      return
    }

    setUploading(true)
    setUploadResult(null)
    
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/tenders/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      setUploadResult(response.data)
      fetchUploadLogs()
      
      if (response.data.created > 0) {
        toast.success(`Successfully uploaded ${response.data.created} tenders`)
      } else if (response.data.duplicates > 0) {
        toast.info(`All ${response.data.duplicates} records were duplicates`)
      }
    } catch (error) {
      console.error('Bulk upload failed:', error)
      toast.error(error.response?.data?.error || 'Failed to upload tenders')
      setUploadResult({ error: error.response?.data?.error || 'Upload failed' })
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Bulk Upload Tenders</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Supported formats:</strong> Excel (.xlsx, .xls) and CSV files
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Excel files can have multiple sheets - each sheet will be processed as a separate category.
            Expected columns: Bid Number, Work Description, Organisation Name, Bid Submission End Date, etc.
          </p>
        </div>

        {/* File Upload Area */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Upload File
          </label>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="bulk-upload"
            />
            <label htmlFor="bulk-upload" className="cursor-pointer">
              <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
              {file ? (
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatFileSize(file.size)} • Click or drag to replace
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isDragging ? 'Drop your file here!' : 'Click to select or drag & drop'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    .xlsx, .xls, or .csv (max 25MB)
                  </p>
                </div>
              )}
            </label>
          </div>
        </div>

        {/* Upload Result */}
        {uploadResult && !uploadResult.error && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">Upload Complete</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                <p className="text-2xl font-bold text-green-600">{uploadResult.created}</p>
                <p className="text-xs text-gray-500">Created</p>
              </div>
              <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                <p className="text-2xl font-bold text-yellow-600">{uploadResult.duplicates}</p>
                <p className="text-xs text-gray-500">Duplicates</p>
              </div>
              <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                <p className="text-2xl font-bold text-red-600">{uploadResult.errors}</p>
                <p className="text-xs text-gray-500">Errors</p>
              </div>
              <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                <p className="text-2xl font-bold text-blue-600">{uploadResult.totalProcessed}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            
            {/* Sheets Processed */}
            {uploadResult.sheetsProcessed && uploadResult.sheetsProcessed.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Sheets Processed:</p>
                <div className="flex flex-wrap gap-2">
                  {uploadResult.sheetsProcessed.map((sheet, i) => (
                    <span key={i} className="px-2 py-1 bg-white dark:bg-gray-700 rounded text-xs">
                      <strong>{sheet.sheetName}</strong>: {sheet.successCount}/{sheet.rowCount}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Error Details */}
            {uploadResult.errorDetails && uploadResult.errorDetails.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Errors (first 20):</p>
                <div className="max-h-32 overflow-y-auto text-xs bg-white dark:bg-gray-800 rounded p-2">
                  {uploadResult.errorDetails.map((err, i) => (
                    <p key={i} className="text-red-600 dark:text-red-400">
                      [{err.sheet}] Row {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">Processing time: {uploadResult.processingTime}</p>
          </div>
        )}

        {uploadResult?.error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-300">{uploadResult.error}</p>
          </div>
        )}

        {/* Upload History Toggle */}
        <div className="mb-4">
          <button
            onClick={() => setShowLogs(!showLogs)}
            className="flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <Clock className="w-4 h-4 mr-1" />
            {showLogs ? 'Hide' : 'Show'} Upload History
          </button>
        </div>

        {/* Upload Logs */}
        {showLogs && uploadLogs.length > 0 && (
          <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">File</th>
                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Uploaded By</th>
                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Date</th>
                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Results</th>
                  <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {uploadLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      <div className="flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          log.fileType === 'xlsx' ? 'bg-green-500' : 
                          log.fileType === 'xls' ? 'bg-green-400' : 'bg-blue-500'
                        }`}></span>
                        <span className="truncate max-w-[120px]" title={log.originalName}>
                          {log.originalName}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{log.uploadedByName}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {format(new Date(log.timestamp), 'MMM dd, HH:mm')}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-green-600">{log.successCount}</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-gray-600 dark:text-gray-400">{log.totalRecords}</span>
                      {log.duplicateCount > 0 && (
                        <span className="text-yellow-600 ml-1">({log.duplicateCount} dup)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        log.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        log.status === 'partial' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        log.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            {uploadResult?.created > 0 ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Tenders
              </>
            )}
          </button>
        </div>

        {uploadResult?.created > 0 && (
          <button
            onClick={() => {
              onSuccess()
              onClose()
            }}
            className="w-full mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            View Uploaded Tenders
          </button>
        )}
      </div>
    </div>
  )
}

// Assign Modal Component
const AssignModal = ({ tender, users, onAssign, onUnassign, onClose }) => {
  if (!tender) return null
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Assign Tender</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
          {tender.title}
        </p>

        {tender.assignedTo && (
          <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-sm text-purple-700 dark:text-purple-300">
              Currently assigned to: <strong>{tender.assignedTo.name || 'Unknown'}</strong>
            </p>
            <button
              onClick={() => onUnassign(tender)}
              className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
            >
              Remove assignment
            </button>
          </div>
        )}

        <div className="space-y-2 max-h-64 overflow-y-auto">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select a team member:</p>
          {users.map(user => (
            <button
              key={user._id}
              onClick={() => onAssign(user._id)}
              className={`w-full flex items-center p-3 rounded-lg border transition-colors ${
                tender.assignedTo?._id === user._id
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center mr-3">
                <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                  {user.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user.role} • {user.department}</p>
              </div>
              {tender.assignedTo?._id === user._id && (
                <Check className="w-5 h-5 text-purple-600 ml-auto" />
              )}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default Tenders
